import { Inject, Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import makeDebug from '../../../../makeDebug';
import { SessionController } from '../../../auth/session/session-controller';
import { AccountService } from '../account.service';
import { IFeathersAppProvider } from '../contracts/sync/feathers-app-provider';
import { FeathersService } from '../feathers.service';
import { Chat } from './model/chat-instance';

const debug = makeDebug('services:chat:user');

@Injectable({ providedIn: 'root' })
export class ChatUserService {
  get localUserChatToken$(): Observable<string> {
    return this._localUserChatToken$.asObservable();
  }

  get localAgentChatToken$(): Observable<string> {
    return this._localAgentChatToken$.asObservable();
  }

  private _localUserIdentity$ = new ReplaySubject<string>();
  private _localUserChatToken$ = new ReplaySubject<string>();
  private _localAgentChatToken$ = new ReplaySubject<string>();

  constructor(
    private readonly _accountService: AccountService,
    private readonly _session: SessionController,
    private readonly _platform: Platform,
    @Inject(FeathersService) private readonly _feathersApp: IFeathersAppProvider
  ) {
    this._session.isAuthenticated$
      .pipe(filter(authenticated => authenticated))
      .subscribe(() => this.updateUserIdentity());
    this._session.state$
      .pipe(filter(state => state === 'online'))
      .subscribe(() => void this.updateAccountChatInformation());
  }

  public async getUserDisplayName(): Promise<string> {
    return await this._accountService.getUserFullName();
  }

  public getUserIdentity(): Promise<string> {
    debug('get user chat identity');
    return this._localUserIdentity$.pipe(take(1)).toPromise();
  }

  public async refreshToken(chat: Chat): Promise<string> {
    await this._session.whenAuthenticated();

    debug('refreshing token...');
    if (chat === Chat.PatientApp) {
      const chatToken = await this.getAgentChatToken();
      if (!chatToken) {
        throw Error('refreshing failed');
      }
      this._localAgentChatToken$.next(chatToken);
      return chatToken;
    } else {
      throw new Error(`Unknown chat type: ${chat}`);
    }
  }
  private async updateAccountChatInformation(): Promise<void> {
    try {
      const account = await this._accountService.getAccount();
      if (account) {
        const agentChatToken = await this.getAgentChatToken();

        debug('update account information', { agentChatToken });

        if (agentChatToken) {
          this._localAgentChatToken$.next(agentChatToken);
        }
      }
    } catch (error) {
      window.logger.error('[ChatUserService] updateAccountChatInformation failed', error);
    }
  }

  private async getAgentChatToken(): Promise<string> {
    if (!(await this._accountService.isAgent())) {
      return 'NO_AGENT';
    }

    try {
      const { token } = await this._feathersApp.app.service('agent-chat').create({
        pushChannel: this._platform.is('ios') ? 'apn' : 'fcm',
      });
      return token;
    } catch (e) {
      return null;
    }
  }

  private async updateUserIdentity(): Promise<void> {
    const _id = await this._accountService.getCurrentUserId();

    if (_id) {
      this._localUserIdentity$.next(_id);
    }
  }
}
